import { useState } from "react";

/**
 * Custom hook for managing booking recommendations
 * Handles recommendations state, worker filtering, pagination, and lazy-loaded routes
 */
export default function useBookingRecommendations() {
  const [bookingRequest, setBookingRequest] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filter states
  const [filterAfterDate, setFilterAfterDate] = useState('');
  const [filterAfterHour, setFilterAfterHour] = useState('');
  const [filterBeforeHour, setFilterBeforeHour] = useState('');

  // Route data state
  const [recommendationRoutes, setRecommendationRoutes] = useState({});
  const [loadingRouteIds, setLoadingRouteIds] = useState(new Set());

  // Fetch route for a single recommendation (called when scrolled into view)
  const fetchRouteForRecommendation = async (recommendationId) => {
    // Don't fetch if already loading or already loaded
    if (loadingRouteIds.has(recommendationId) || recommendationRoutes[recommendationId]) {
      return;
    }

    // Mark as loading
    setLoadingRouteIds(prev => new Set(prev).add(recommendationId));

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

      const response = await fetch(`/api/last_minute_booking_recommendations/${recommendationId}/calculate_route`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Store route data for this recommendation
          setRecommendationRoutes(prev => ({
            ...prev,
            [recommendationId]: data.data.routes
          }));
        }
      }
    } catch (error) {
      console.error(`Error fetching route for recommendation ${recommendationId}:`, error);
    } finally {
      // Remove from loading set
      setLoadingRouteIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(recommendationId);
        return newSet;
      });
    }
  };

  // Filter recommendations by selected workers, date, and time
  const filterRecommendations = () => {
    return recommendations.filter((rec) => {
      // Worker filter
      if (selectedWorkerIds.length > 0 && !selectedWorkerIds.includes(rec.worker.id)) {
        return false;
      }

      // After date filter
      if (filterAfterDate) {
        const recDate = new Date(rec.arrival_window_start || rec.proposed_start_time);
        const filterDate = new Date(filterAfterDate);
        if (recDate < filterDate) {
          return false;
        }
      }

      // After hour filter
      if (filterAfterHour !== '' && filterAfterHour !== null) {
        const recDate = new Date(rec.arrival_window_start || rec.proposed_start_time);
        const recHour = recDate.getHours();
        if (recHour < parseInt(filterAfterHour)) {
          return false;
        }
      }

      // Before hour filter
      if (filterBeforeHour !== '' && filterBeforeHour !== null) {
        const recDate = new Date(rec.arrival_window_start || rec.proposed_start_time);
        const recHour = recDate.getHours();
        if (recHour > parseInt(filterBeforeHour)) {
          return false;
        }
      }

      return true;
    });
  };

  // Paginate filtered recommendations
  const paginateRecommendations = (filteredRecs) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredRecs.slice(startIndex, endIndex);
  };

  // Get filtered and paginated recommendations
  const getDisplayedRecommendations = () => {
    const filtered = filterRecommendations();
    return {
      filtered,
      paginated: paginateRecommendations(filtered),
      totalPages: Math.ceil(filtered.length / itemsPerPage),
    };
  };

  // Reset recommendations and related state
  const resetRecommendations = () => {
    setBookingRequest(null);
    setRecommendations([]);
    setRecommendationRoutes({});
    setLoadingRouteIds(new Set());
    setCurrentPage(1);
    setSelectedWorkerIds([]);
    setFilterAfterDate('');
    setFilterAfterHour('');
    setFilterBeforeHour('');
  };

  // Get route data for a specific recommendation
  const getRouteData = (recommendationId) => {
    return recommendationRoutes[recommendationId];
  };

  // Check if a route is currently loading
  const isRouteLoading = (recommendationId) => {
    return loadingRouteIds.has(recommendationId);
  };

  return {
    // State
    bookingRequest,
    setBookingRequest,
    recommendations,
    setRecommendations,

    // Worker filter
    selectedWorkerIds,
    setSelectedWorkerIds,

    // Date/Time filters
    filterAfterDate,
    setFilterAfterDate,
    filterAfterHour,
    setFilterAfterHour,
    filterBeforeHour,
    setFilterBeforeHour,

    // Pagination
    currentPage,
    setCurrentPage,
    itemsPerPage,

    // Routes
    recommendationRoutes,
    fetchRouteForRecommendation,
    getRouteData,
    isRouteLoading,

    // Helpers
    getDisplayedRecommendations,
    resetRecommendations,
  };
}
